"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
const Logger_1 = require("./Logger");
const events_1 = require("events");
const consoleInterceptor = Logger_1.ConsoleInterceptor.interceptConsoleLogs(`[MediaSoup] - `);
const mediasoup_client_1 = require("mediasoup-client");
consoleInterceptor.restoreConsole();
var RequestType;
(function (RequestType) {
    RequestType["CREATE_TRANSPORT"] = "createTransport";
    RequestType["TRANSPORT_CONNECT"] = "transportConnect";
    RequestType["PRODUCE_DATA"] = "produceData";
    RequestType["CONSUME_DATA"] = "consumeData";
    RequestType["PRODUCE_STREAM"] = "produceStream";
    RequestType["CONSUME_STREAM"] = "consumeStream";
    RequestType["CLOSE_STREAM"] = "closeStream";
})(RequestType = exports.RequestType || (exports.RequestType = {}));
class Client extends events_1.EventEmitter {
    constructor(url, roomId, userId, type, logger) {
        super();
        this.device = new mediasoup_client_1.Device();
        this.reconnectAttempts = 0;
        this.closed = false;
        if (logger)
            consoleInterceptor.setLogger(logger);
        const loggerContextName = `${type}_${userId}`;
        consoleInterceptor.addContext(loggerContextName, this.loggerContext.bind(this));
        this.on("close", () => {
            setTimeout(() => consoleInterceptor.removeContext(loggerContextName), 5000);
        });
        this.url = url;
        this.roomId = roomId;
        this.userId = userId;
        this.clientType = type;
        this.transport = this.initTransport();
    }
    initTransport() {
        return this.createTransport(this.clientType)
            .then((t) => __awaiter(this, void 0, void 0, function* () { yield this.init(t); return t; }))
            .catch(e => {
            this.close(e);
            return Promise.reject(e);
        });
    }
    callServerAPI(type, request) {
        return __awaiter(this, void 0, void 0, function* () {
            return (yield (yield fetch(this.url + "&type=" + type, {
                method: "POST",
                headers: {
                    Accept: "application/json",
                    "Content-Type": "application/json"
                },
                body: JSON.stringify(Object.assign(Object.assign({}, request), { roomId: this.roomId, userId: this.userId }))
            })).json());
        });
    }
    serverProduceStream(data) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.callServerAPI(RequestType.PRODUCE_STREAM, data);
        });
    }
    serverConsumeStream(data) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.callServerAPI(RequestType.CONSUME_STREAM, data);
        });
    }
    serverProduceData(data) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.callServerAPI(RequestType.PRODUCE_DATA, data);
        });
    }
    serverConsumeData(data) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.callServerAPI(RequestType.CONSUME_DATA, data);
        });
    }
    serverCloseStream(data) {
        return __awaiter(this, void 0, void 0, function* () {
            this.callServerAPI(RequestType.CLOSE_STREAM, data);
        });
    }
    createTransport(type) {
        return __awaiter(this, void 0, void 0, function* () {
            const dto = yield this.callServerAPI(RequestType.CREATE_TRANSPORT, { type });
            let transport;
            this.device = new mediasoup_client_1.Device();
            yield this.device.load({
                routerRtpCapabilities: dto.capabilities
            });
            const options = {
                id: dto.transport.id,
                iceParameters: dto.transport.iceParameters,
                iceCandidates: dto.transport.iceCandidates,
                dtlsParameters: dto.transport.dtlsParameters,
                sctpParameters: dto.transport.sctpParameters
            };
            if (type === "producer") {
                transport = this.device.createSendTransport(options);
            }
            else {
                transport = this.device.createRecvTransport(options);
            }
            transport.on("connect", ({ dtlsParameters }, callback, errback) => __awaiter(this, void 0, void 0, function* () {
                try {
                    yield this.callServerAPI(RequestType.TRANSPORT_CONNECT, { dtlsParameters });
                    callback();
                }
                catch (e) {
                    errback(e);
                }
            }));
            const _that = this;
            transport.on("connectionstatechange", function handler(state) {
                _that.emit("connectionstatechange", state);
                if (state === "connected") {
                    _that.reconnectAttempts = 0;
                }
                else if (["closed", "failed", "disconnected"].includes(state)) {
                    transport.removeListener("connectionstatechange", handler);
                    if (_that.reconnectAttempts < 10) {
                        _that.reconnectAttempts++;
                        _that.transport = _that.initTransport();
                    }
                    else {
                        _that.close(new Error("Connection state changed to " + state));
                    }
                }
            });
            return transport;
        });
    }
    awaitConnected() {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.transport;
            return new Promise((resolve, reject) => {
                const checkStatus = () => __awaiter(this, void 0, void 0, function* () {
                    if (this.closed) {
                        reject(new Error("Client closed"));
                        return;
                    }
                    try {
                        const transport = yield this.transport;
                        if (transport.connectionState === "connected") {
                            resolve(transport);
                            return;
                        }
                        if (["closed", "failed", "disconnected"].includes(transport.connectionState)) {
                            reject(new Error("Transport connection state is " + transport.connectionState));
                            return;
                        }
                        setTimeout(checkStatus, 50);
                    }
                    catch (e) {
                        reject(e);
                    }
                });
                checkStatus();
            });
        });
    }
    getStats() {
        return __awaiter(this, void 0, void 0, function* () {
            return Array.from((yield (yield this.transport).getStats()).values());
        });
    }
    close(reason) {
        if (!reason)
            reason = new Error("client.close() called directly");
        this.emit("close", reason);
        this.transport.then(transport => transport.close())
            .catch(() => { });
        this.closed = true;
    }
    static retryHelper(call, { count, timeout } = { count: 60, timeout: 500 }) {
        return __awaiter(this, void 0, void 0, function* () {
            let lastError;
            for (let i = 0; i < count; i++) {
                try {
                    return yield call();
                }
                catch (e) {
                    lastError = e;
                    if (i < count - 1)
                        yield new Promise(resolve => setTimeout(resolve, timeout));
                }
            }
            throw lastError;
        });
    }
}
exports.Client = Client;
